import React, { memo } from 'react';
import {
  ActionIcon,
  Box,
  Checkbox,
  NumberInput,
  Select,
  Table,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import {
  PLANNING_RESOURCE_TYPES,
  type PlanningResource,
  type PlanningResourceType,
} from '../../../api/planningResourceApis';

export type ResourceRowDraft = Partial<
  Pick<
    PlanningResource,
    | 'name'
    | 'type'
    | 'unitOfMeasure'
    | 'maxUnits'
    | 'maxUnitsPerDay'
    | 'standardRate'
    | 'overtimeRate'
    | 'costPerUse'
    | 'initials'
    | 'email'
    | 'calendarId'
    | 'notes'
  >
>;

const TYPE_OPTIONS = PLANNING_RESOURCE_TYPES.map((value) => ({
  value,
  label: value.charAt(0) + value.slice(1).toLowerCase(),
}));

const COL = {
  check: 40,
  name: 300,
  type: 108,
  unit: 52,
  maxUnits: 84,
  maxDay: 70,
  std: 92,
  ot: 92,
  cost: 92,
  initials: 72,
  email: 118,
  calendar: 220,
  notes: 140,
  actions: 44,
} as const;

export type ResourceTableRowProps = {
  row: PlanningResource;
  dirty: boolean;
  selected: boolean;
  calendarOptions: Array<{ value: string; label: string }>;
  saving: boolean;
  onToggleSelect: (id: string) => void;
  onPatch: (id: string, patch: ResourceRowDraft) => void;
  onDelete: (row: PlanningResource) => void;
};

function ResourceTableRowComponent({
  row,
  dirty,
  selected,
  calendarOptions,
  saving,
  onToggleSelect,
  onPatch,
  onDelete,
}: ResourceTableRowProps) {
  return (
    <Table.Tr
      className={[dirty ? 'rm-row--dirty' : '', selected ? 'rm-row--selected' : '']
        .filter(Boolean)
        .join(' ')}
    >
      <Table.Td
        className="rm-col--sticky rm-col--check"
        style={{ width: COL.check, minWidth: COL.check }}
      >
        <Checkbox
          size="xs"
          aria-label={`Select ${row.name || 'resource'}`}
          checked={selected}
          onChange={() => onToggleSelect(row.id)}
        />
      </Table.Td>
      <Table.Td
        className="rm-col--sticky rm-col--name"
        style={{ minWidth: COL.name, width: COL.name }}
      >
        <TextInput
          className="rm-ghost rm-ghost--name"
          variant="unstyled"
          value={row.name}
          aria-label="Resource name"
          onChange={(e) => onPatch(row.id, { name: e.currentTarget.value })}
        />
      </Table.Td>
      <Table.Td style={{ minWidth: COL.type, width: COL.type }}>
        <Select
          className="rm-ghost rm-type-select"
          variant="unstyled"
          data={TYPE_OPTIONS}
          value={String(row.type || 'WORK')}
          allowDeselect={false}
          aria-label="Resource type"
          onChange={(value) =>
            onPatch(row.id, { type: (value as PlanningResourceType) || 'WORK' })
          }
        />
      </Table.Td>
      <Table.Td style={{ minWidth: COL.unit, width: COL.unit }}>
        <TextInput
          className="rm-ghost"
          variant="unstyled"
          value={row.unitOfMeasure}
          aria-label="Unit of measure"
          onChange={(e) => onPatch(row.id, { unitOfMeasure: e.currentTarget.value })}
        />
      </Table.Td>
      <Table.Td style={{ minWidth: COL.maxUnits, width: COL.maxUnits }}>
        <NumberInput
          className="rm-ghost rm-ghost--num"
          variant="unstyled"
          value={row.maxUnits}
          hideControls
          decimalScale={2}
          aria-label="Max units"
          onChange={(value) =>
            onPatch(row.id, { maxUnits: typeof value === 'number' ? value : 0 })
          }
        />
      </Table.Td>
      <Table.Td style={{ minWidth: COL.maxDay, width: COL.maxDay }}>
        <NumberInput
          className="rm-ghost rm-ghost--num"
          variant="unstyled"
          value={row.maxUnitsPerDay}
          hideControls
          decimalScale={2}
          aria-label="Max units per day"
          onChange={(value) =>
            onPatch(row.id, { maxUnitsPerDay: typeof value === 'number' ? value : 0 })
          }
        />
      </Table.Td>
      <Table.Td style={{ minWidth: COL.std, width: COL.std }}>
        <NumberInput
          className="rm-ghost rm-ghost--num"
          variant="unstyled"
          value={row.standardRate}
          hideControls
          decimalScale={2}
          aria-label="Standard rate"
          onChange={(value) =>
            onPatch(row.id, { standardRate: typeof value === 'number' ? value : 0 })
          }
        />
      </Table.Td>
      <Table.Td style={{ minWidth: COL.ot, width: COL.ot }}>
        <NumberInput
          className="rm-ghost rm-ghost--num"
          variant="unstyled"
          value={row.overtimeRate}
          hideControls
          decimalScale={2}
          aria-label="Overtime rate"
          onChange={(value) =>
            onPatch(row.id, { overtimeRate: typeof value === 'number' ? value : 0 })
          }
        />
      </Table.Td>
      <Table.Td style={{ minWidth: COL.cost, width: COL.cost }}>
        <NumberInput
          className="rm-ghost rm-ghost--num"
          variant="unstyled"
          value={row.costPerUse}
          hideControls
          decimalScale={2}
          aria-label="Cost per use"
          onChange={(value) =>
            onPatch(row.id, { costPerUse: typeof value === 'number' ? value : 0 })
          }
        />
      </Table.Td>
      <Table.Td style={{ minWidth: COL.initials, width: COL.initials }}>
        <TextInput
          className="rm-ghost"
          variant="unstyled"
          value={row.initials}
          aria-label="Initials"
          onChange={(e) => onPatch(row.id, { initials: e.currentTarget.value })}
        />
      </Table.Td>
      <Table.Td style={{ minWidth: COL.calendar, width: COL.calendar }}>
        <Select
          className="rm-ghost rm-select-clearable"
          variant="unstyled"
          data={calendarOptions}
          value={row.calendarId || null}
          placeholder="Calendar"
          searchable
          clearable
          aria-label="Calendar"
          onChange={(value) => onPatch(row.id, { calendarId: value || '' })}
        />
      </Table.Td>
      <Table.Td style={{ minWidth: COL.notes, width: COL.notes }}>
        <TextInput
          className="rm-ghost"
          variant="unstyled"
          value={row.notes ?? ''}
          aria-label="Notes"
          onChange={(e) => onPatch(row.id, { notes: e.currentTarget.value || null })}
        />
      </Table.Td>
      <Table.Td style={{ minWidth: COL.email, width: COL.email }}>
        <TextInput
          className="rm-ghost"
          variant="unstyled"
          value={row.email ?? ''}
          aria-label="Email"
          onChange={(e) => onPatch(row.id, { email: e.currentTarget.value || null })}
        />
      </Table.Td>
      <Table.Td style={{ width: COL.actions }}>
        <Box className="rm-actions">
          <Tooltip label="Delete resource">
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              radius="md"
              aria-label="Delete resource"
              onClick={() => onDelete(row)}
              disabled={saving}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Tooltip>
        </Box>
      </Table.Td>
    </Table.Tr>
  );
}

export const ResourceTableRow = memo(ResourceTableRowComponent);
export { COL as RESOURCE_TABLE_COL };
